import { PageHead } from "@generalapplications/html-render/react";
import { PageArticle, Stack } from "@generalapplications/ui";
import { parseArticleDetail } from "@general-applications/contracts/content";
import { useLoaderData } from "react-router-dom";

import { ArticleContactCta } from "@/app/pages/blogDetail/components/ArticleContactCta";
import { ArticleHeader } from "@/app/pages/blogDetail/components/ArticleHeader";
import { DataContractBoundaryQuarantineVisual } from "@/app/features/diagrams/components/blog/DataContractBoundaryQuarantineVisual";
import { DecomposedCompoundAiArchitectureVisual } from "@/app/features/diagrams/components/blog/DecomposedCompoundAiArchitectureVisual";
import { DeterministicControlsArchitectureVisual } from "@/app/features/diagrams/components/blog/DeterministicControlsArchitectureVisual";
import { StateMachineVerificationPipelineVisual } from "@/app/features/diagrams/components/blog/StateMachineVerificationPipelineVisual";
import { S1000DConversionPipelineVisual } from "@/app/features/diagrams/components/blog/S1000DConversionPipelineVisual";
import { HybridSearchRetrievalPipelineVisual } from "@/app/features/diagrams/components/blog/HybridSearchRetrievalPipelineVisual";
import { RelatedArticles } from "@/app/pages/blogDetail/components/RelatedArticles";
import { ArticleContent } from "@/app/features/content/components/ArticleContent";
import type { FigureComponentRegistry } from "@/app/features/content/components/RichContent";
import { Reveal } from "@/app/common/components/composite/motion/Reveal";
import { blogPosts, isPreviewableBlogPost } from "@/content/blog";
import { routes } from "@/app/common/functions/routes/routes";
import { pageHead, sectionTrail } from "@/app/common/functions/seo/pageHead";
import { NotFoundPage } from "@/app/pages/notFound/NotFoundPage";

import { DmcFacetedDecompositionVisual } from "@/app/features/diagrams/components/blog/DmcFacetedDecompositionVisual";
import { IssueBrexIntakeGateVisual } from "@/app/features/diagrams/components/blog/IssueBrexIntakeGateVisual";
import { OfflinePackageUpdateVisual } from "@/app/features/diagrams/components/blog/OfflinePackageUpdateVisual";
import { GeometryBindingGapVisual } from "@/app/features/diagrams/components/blog/GeometryBindingGapVisual";

import { WorkManagementLifecycleVisual } from "@/app/features/diagrams/components/blog/WorkManagementLifecycleVisual";

const blogFigureComponents: FigureComponentRegistry = {
  "dmc-faceted-decomposition": DmcFacetedDecompositionVisual,
  "s1000d-issue-brex-intake": IssueBrexIntakeGateVisual,
  "s1000d-offline-package-update": OfflinePackageUpdateVisual,
  "s1000d-geometry-binding-gap": GeometryBindingGapVisual,

  "deterministic-controls-agent-architecture":
    DeterministicControlsArchitectureVisual,
  "hybrid-search-retrieval-pipeline": HybridSearchRetrievalPipelineVisual,
  "data-contract-boundary-quarantine": DataContractBoundaryQuarantineVisual,
  "decomposed-compound-ai-architecture": DecomposedCompoundAiArchitectureVisual,
  "state-machine-verification-pipeline": StateMachineVerificationPipelineVisual,
  "s1000d-conversion-pipeline": S1000DConversionPipelineVisual,
  "work-management-lifecycle": WorkManagementLifecycleVisual,
};

export function BlogDetailPage() {
  const post = parseArticleDetail(useLoaderData());

  if (!post) return <NotFoundPage />;

  const path = routes.blog.detail(post.slug);

  const relatedPosts = blogPosts
    .filter((item) => item.slug !== post.slug && isPreviewableBlogPost(item))
    .map((item) => ({
      post: item,
      score: item.tags.filter((tag) => post.tags.includes(tag)).length,
    }))
    .sort((left, right) => right.score - left.score)
    .slice(0, 2)
    .map(({ post: relatedPost }) => relatedPost);

  return (
    // The page is full width so the prose column keeps its measure beside
    // the table of contents; the headline sits in that same column.
    <PageArticle width="full">
      <PageHead
        head={pageHead({
          path,
          title: post.seoTitle,
          description: post.seoDescription,
          imagePath: post.image,
          breadcrumbs: sectionTrail(
            { name: "Blog", path: routes.blog.index },
            { name: post.title, path },
          ),
          article: post,
        })}
      />
      <Reveal>
        <Stack gap="xxl">
          <ArticleContent
            lead={<ArticleHeader post={post} />}
            blocks={post.content}
            figureComponents={blogFigureComponents}
          />
          <RelatedArticles posts={relatedPosts} />
          <ArticleContactCta />
        </Stack>
      </Reveal>
    </PageArticle>
  );
}
